import { Table, Button, Tooltip, Grid } from 'antd';
import type { TableProps, ColumnType } from 'antd/es/table';
import { SettingOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useColumnSettings } from '@/hooks/useColumnSettings';
import { useAuth } from '@/hooks/useAuth';
import { ColumnSettingsDrawer } from './ColumnSettingsDrawer';

const { useBreakpoint } = Grid;

export interface ERPColumnDef<T> extends ColumnType<T> {
  key: string;
  hideable?: boolean;      // can be hidden by user
  renameable?: boolean;    // can be renamed by user
  alwaysVisible?: boolean; // cannot be hidden
  hideOnMobile?: boolean;
}

interface ERPTableProps<T> extends Omit<TableProps<T>, 'columns'> {
  tableKey: string;
  columns: ERPColumnDef<T>[];
  actionColumn?: ColumnType<T>;  // always fixed right
  fitHeight?: boolean;
  bottomOffset?: number;
  hideSettingsButton?: boolean;
  registerOpenSettings?: (open: () => void) => void;
  /** Business id accessor used to build row data-testid (defaults to rowKey or _id). */
  testIdAccessor?: (row: T) => string | number | undefined;
}

export function ERPTable<T extends object>({
  tableKey,
  columns,
  actionColumn,
  scroll,
  fitHeight = false,
  bottomOffset = 16,
  hideSettingsButton = false,
  registerOpenSettings,
  testIdAccessor,
  onRow,
  ...tableProps
}: ERPTableProps<T>) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrollY, setScrollY] = useState<number>();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { user } = useAuth();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const openSettingsDrawer = useCallback(() => setDrawerOpen(true), []);

  useEffect(() => {
    if (!fitHeight) return;
    const el = wrapperRef.current;
    if (!el) return;

    const calc = () => {
      const wrapperH = el.offsetHeight;
      const thead = el.querySelector<HTMLElement>('.ant-table-thead');
      const pagination = el.querySelector<HTMLElement>('.ant-table-pagination');
      const theadH = thead?.offsetHeight ?? 39;
      const paginationH = pagination?.offsetHeight ?? 0;
      const paginationMargin = paginationH > 0 ? 16 : 0; // ant margin-top on pagination
      const h = Math.max(220, wrapperH - theadH - paginationH - paginationMargin - bottomOffset);
      setScrollY(h);
    };

    const ro = new ResizeObserver(calc);
    ro.observe(el);
    calc();
    return () => ro.disconnect();
  }, [fitHeight, bottomOffset, screens.md, screens.lg]);

  useEffect(() => {
    if (!registerOpenSettings) return;
    registerOpenSettings(openSettingsDrawer);
  }, [registerOpenSettings, openSettingsDrawer]);

  // Only include columns the user can actually configure (not alwaysVisible = cannot be hidden)
  const initialCols = columns
    .filter((c) => !c.alwaysVisible && (c.hideable === true || c.renameable === true))
    .map((c) => ({
      key: String(c.key),
      title: String(c.title ?? c.key),
      width: typeof c.width === 'number' ? c.width : undefined,
    }));

  const { settings, setVisible, setDisplayTitle, setWidth, reorder, reset } = useColumnSettings(
    tableKey,
    initialCols,
    { userId: user?.id, tenantId: user?.tenantId }
  );

  const settingMap = Object.fromEntries(settings.map((s) => [s.key, s]));
  const baseIndexMap = useMemo(
    () => Object.fromEntries(columns.map((col, idx) => [String(col.key), idx])),
    [columns]
  );
  const configurableSlots = useMemo(
    () => columns.flatMap((col, idx) => (!col.alwaysVisible ? [idx] : [])),
    [columns]
  );

  const visibleColumns = columns
    .filter((col) => {
      if (isMobile && col.hideOnMobile && !col.alwaysVisible) return false;
      const s = settingMap[String(col.key)];
      if (col.alwaysVisible) return true;
      if (!s) return true;
      return s.visible;
    })
    .sort((a, b) => {
      const ka = String(a.key);
      const kb = String(b.key);
      const sa = settingMap[ka];
      const sb = settingMap[kb];
      const baseA = baseIndexMap[ka] ?? 999;
      const baseB = baseIndexMap[kb] ?? 999;
      const orderA = sa ? (configurableSlots[sa.order] ?? baseA) : baseA;
      const orderB = sb ? (configurableSlots[sb.order] ?? baseB) : baseB;
      return orderA - orderB;
    })
    .map((col) => {
      const s = settingMap[String(col.key)];
      return {
        ...col,
        title: s?.displayTitle ?? col.title,
        width: s?.width ?? col.width,
      };
    });

  const finalColumns: ColumnType<T>[] = [
    ...visibleColumns,
    ...(actionColumn
      ? [{ ...actionColumn, fixed: 'right' as const, key: '__action', width: actionColumn.width ?? (isMobile ? 88 : 120) }]
      : []),
  ];

  const settingsBtn = hideSettingsButton ? null : (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 8,
      }}
    >
      <Tooltip title="Tuỳ chỉnh cột">
        <Button
          size="small"
          icon={<SettingOutlined />}
          onClick={() => setDrawerOpen(true)}
        />
      </Tooltip>
    </div>
  );

  const mergedScroll = useMemo(() => {
    // Compute total width from actual visible columns so width changes apply immediately
    const totalColWidth = finalColumns.reduce((sum, col) => {
      return sum + (typeof col.width === 'number' ? col.width : 120);
    }, 0);
    const baseX = totalColWidth > 0 ? totalColWidth : ((scroll as any)?.x ?? 'max-content');
    const baseY = (scroll as any)?.y;
    return {
      x: baseX,
      y: fitHeight ? (scrollY ?? baseY) : baseY,
    };
  }, [scroll, fitHeight, scrollY, finalColumns]);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: fitHeight ? 1 : undefined,
        minHeight: fitHeight ? 0 : undefined,
      }}
    >
      {settingsBtn}
      <Table<T>
        {...tableProps}
        columns={finalColumns}
        scroll={mergedScroll}
        sticky
        onRow={(record, index) => {
          const base = onRow?.(record, index) ?? {};
          const id =
            testIdAccessor?.(record) ??
            (record as { _id?: string | number; id?: string | number })._id ??
            (record as { id?: string | number }).id;
          return {
            ...base,
            'data-testid': id != null ? `${tableKey}-row-${id}` : `${tableKey}-row-${index}`,
          } as React.HTMLAttributes<HTMLElement>;
        }}
      />
      <ColumnSettingsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        settings={settings}
        onSetVisible={setVisible}
        onSetDisplayTitle={setDisplayTitle}
        onSetWidth={setWidth}
        onReorder={reorder}
        onReset={reset}
      />
    </div>
  );
}
